import { Form } from 'lib/components/Form';
import { replaceUndefinedWithDelete } from 'lib/helpers';
import { EOrganization, ESnapshotExists } from 'lib/types';
import React from 'react';
import { getFirebaseContext } from 'utils/firebase';
import {
  CardGridLayout,
  GridInput,
  useEditableCard
} from 'lib/components/Card/Grid';
import LabeledSwitch from 'lib/components/LabeledSwitch';

const getGeneralBillingSettingsFromOrg = (
  organization: ESnapshotExists<EOrganization>
): {
  requireUpfrontPayment: boolean;
  sendInvoiceReminders: boolean;
  autoInvoice: boolean;
  autoConfirmOnAutoInvoice: boolean;
} => {
  const {
    requireUpfrontPayment,
    sendInvoiceReminders,
    autoInvoice,
    autoConfirmOnAutoInvoice
  } = organization.data();

  return {
    requireUpfrontPayment: !!requireUpfrontPayment,
    sendInvoiceReminders: !!sendInvoiceReminders,
    autoInvoice: !!autoInvoice,
    autoConfirmOnAutoInvoice: !!autoConfirmOnAutoInvoice
  };
};

type GeneralBillingSettingsProps = {
  activeOrganization: ESnapshotExists<EOrganization>;
  parentOrganization: ESnapshotExists<EOrganization> | undefined | null;
};

export function GeneralBillingSettings(props: GeneralBillingSettingsProps) {
  const { activeOrganization } = props;

  const currentSettings = getGeneralBillingSettingsFromOrg(activeOrganization);

  const { onChange, onSubmitWrapper, disabled, formSettings, editable } =
    useEditableCard(currentSettings);

  // Shouldn't be possible, but needed for type narrowing.
  if (!formSettings) {
    return null;
  }

  return (
    <Form
      id="publisher-settings-billings-general"
      onSubmit={() =>
        onSubmitWrapper(async () => {
          // Save the new settings on the active organization
          await activeOrganization.ref.update(
            replaceUndefinedWithDelete(getFirebaseContext(), {
              ...formSettings
            })
          );
          return { success: true };
        })
      }
      aria-label="General Settings"
    >
      <CardGridLayout
        header={{
          title: 'General Settings',
          description: 'Configure default billing settings.'
        }}
        editable={editable}
      >
        <GridInput fullWidth>
          <LabeledSwitch
            label="Require upfront payment?"
            description="If enabled, invoices will require upfront payment by default."
            value={formSettings.requireUpfrontPayment}
            onChange={requireUpfrontPayment =>
              onChange({ requireUpfrontPayment })
            }
            disabled={disabled}
          />
        </GridInput>
        <GridInput fullWidth>
          <LabeledSwitch
            label="Automatically generate invoices?"
            description="If enabled, invoices will be created automatically upon notice submission for all notices in this publication."
            value={formSettings.autoInvoice}
            onChange={autoInvoice => onChange({ autoInvoice })}
            disabled={disabled}
          />
        </GridInput>
        {formSettings.autoInvoice && (
          <GridInput fullWidth>
            <LabeledSwitch
              label="Automatically confirm notices?"
              description="If enabled, notices will be auto-confirmed once invoices are successfully created."
              value={formSettings.autoConfirmOnAutoInvoice}
              onChange={autoConfirmOnAutoInvoice =>
                onChange({ autoConfirmOnAutoInvoice })
              }
              disabled={disabled}
            />
          </GridInput>
        )}
        <GridInput fullWidth>
          <LabeledSwitch
            label="Send automatic invoice reminders?"
            description="If enabled, customers with unpaid invoices will receive email notifications every 7 days after their invoice due date."
            value={formSettings.sendInvoiceReminders}
            onChange={sendInvoiceReminders =>
              onChange({ sendInvoiceReminders })
            }
            disabled={disabled}
          />
        </GridInput>
      </CardGridLayout>
    </Form>
  );
}
